/**
 * Active session management. Backed by GET/DELETE /api/auth/sessions.
 *
 * No CSRF handling here: api.ts's request interceptor already attaches X-CSRF-Token to
 * every POST/PUT/PATCH/DELETE (`api.ts:148-162`). Adding it again would be redundant.
 */
import apiClient from './api';
import type { ApiResponse } from '../types';

export interface SessionView {
  id: string;
  name: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  isCurrent: boolean;
}

/**
 * `enabled` mirrors the backend's CREDENTIAL_STORE_ENABLED flag (see auth.controller.ts
 * FIX 1). `false` means the store is off and NO session rows exist anywhere -- an empty
 * `sessions` array in that state means "we are not tracking sessions", not "you have no
 * other sessions", and the two must never be rendered the same way.
 */
export interface SessionsResponse {
  enabled: boolean;
  sessions: SessionView[];
}

export const sessionService = {
  async list(): Promise<SessionsResponse> {
    const res = await apiClient.get<ApiResponse<SessionsResponse>>('/auth/sessions');
    return res.data.data ?? { enabled: false, sessions: [] };
  },

  async revoke(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<null>>(`/auth/sessions/${id}`);
  },

  /**
   * Revokes every other session. Returns `currentSessionKept` from the response — when the
   * backend cannot resolve the caller's own session (e.g. mobile clients, which authenticate
   * with an access token and rarely send the refresh token this resolution needs), it revokes
   * EVERYTHING including the caller's own session and reports `false`. Callers must check this
   * and redirect to login rather than assuming the current session survived.
   */
  async revokeAll(): Promise<boolean> {
    const res = await apiClient.delete<ApiResponse<{ currentSessionKept: boolean }>>('/auth/sessions');
    return res.data.data?.currentSessionKept ?? false;
  },
};
